import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Box, Chip, IconButton, Skeleton, Stack, Tooltip, Typography } from '@mui/material';
import { Delete as DeleteIcon, Edit as EditIcon, FileDownload as FileDownloadIcon, Replay as ReplayIcon } from '@mui/icons-material';
import type { YouTubeSegment } from '@/types/youtubeSegments.types';
import explainerService from '@/services/explainer.service';

/** Format a duration in seconds to a compact readable string, e.g. "5.3s" or "1m 23s". */
function formatDuration(seconds: number): string {
  if (seconds < 60) return `${seconds.toFixed(1)}s`;
  const m = Math.floor(seconds / 60);
  const s = Math.round(seconds % 60).toString().padStart(2, '0');
  return `${m}m ${s}s`;
}

/** Format a timestamp in seconds, e.g. "45.0s" or "1m 09s" once >= 60s. */
function formatTime(seconds: number): string {
  if (seconds < 60) return `${seconds.toFixed(1)}s`;
  const m = Math.floor(seconds / 60);
  const s = Math.round(seconds % 60).toString().padStart(2, '0');
  return `${m}m ${s}s`;
}

/** Determine the chip visual state for a segment. Priority: active > last-worked > proximity > default. */
type ChipState = 'active' | 'last-worked' | 'proximity' | 'default';

function getChipState(
  segId: string,
  activeSegmentId: string | null,
  lastWorkedSegmentId: string | null,
  proximitySegmentId: string | null,
): ChipState {
  if (activeSegmentId === segId) return 'active';
  if (lastWorkedSegmentId === segId) return 'last-worked';
  if (proximitySegmentId === segId) return 'proximity';
  return 'default';
}

/** Map ChipState to MUI Chip variant/color/sx. */
function chipStyleForState(state: ChipState): {
  color: 'primary' | 'default';
  variant: 'filled' | 'outlined';
  extraSx?: object;
} {
  switch (state) {
    case 'active':
      return { color: 'primary', variant: 'filled' };
    case 'last-worked':
      return {
        color: 'primary',
        variant: 'outlined',
        extraSx: { borderLeft: '4px solid', borderColor: 'primary.main' },
      };
    case 'proximity':
      return {
        color: 'default',
        variant: 'outlined',
        extraSx: { outline: '2px solid', outlineColor: 'primary.main' },
      };
    default:
      return { color: 'default', variant: 'outlined' };
  }
}

interface VideoPlayerSegmentListProps {
  segments: YouTubeSegment[];
  isLoading: boolean;
  activeSegmentId: string | null;
  onSegmentClick: (segment: YouTubeSegment) => void;
  onDeleteSegment: (segment: YouTubeSegment) => void;
  /** Story 2.6: The segment's id whose time range contains the current playback time (scan mode). */
  proximitySegmentId?: string | null;
  /** Story 2.6: The id of the last segment the user actively studied (resume mode). */
  lastWorkedSegmentId?: string | null;
  /** Story 2.6: If provided, shows a Resume button in the header that calls onSegmentClick(resumeSegment). */
  resumeSegment?: YouTubeSegment | null;
  /** If provided, shows a Download icon button in the header for practice-reading transcript export. */
  onDownloadTranscripts?: () => void;
  /** Called when the user clicks the edit (pencil) icon on a segment chip. */
  onEditSegment?: (segment: YouTubeSegment) => void;
}

/**
 * VideoPlayerSegmentList — horizontally scrollable segment strip below the YouTube player.
 *
 * Story 2.3 upgrades:
 * - Horizontal scroll container with no chip wrapping (mobile-first, 375px safe).
 * - ARIA tablist/tab semantics with aria-selected for screen readers.
 * - Keyboard navigation: Left/Right Arrow moves focus; Enter/Space selects.
 * - Auto-scrolls the active chip into view when activeSegmentId changes.
 * - Touch targets >= 44×44px; active chip uses primary color (filled vs outlined).
 * - No toasts, no red/error color, no WaveSurfer or podcast patterns.
 *
 * Story 2.6 upgrades:
 * - Three-tier chip visual priority: active > last-worked > proximity > default.
 * - Transcript preview (first 40 chars) in chip label for scan mode.
 * - Resume button in header jumps to last-worked segment in one action.
 */
export const VideoPlayerSegmentList = ({
  segments,
  isLoading,
  activeSegmentId,
  onSegmentClick,
  onDeleteSegment,
  proximitySegmentId = null,
  lastWorkedSegmentId = null,
  resumeSegment = null,
  onDownloadTranscripts,
  onEditSegment,
}: VideoPlayerSegmentListProps) => {
  const { t } = useTranslation('player');
  const stripRef = useRef<HTMLDivElement>(null);
  const [explanationBySegment, setExplanationBySegment] = useState<Record<number, boolean>>({});

  // Auto-scroll the active chip to center when activeSegmentId changes (AC 4).
  // inline:'center' keeps the selected chip in the middle of the strip; when the
  // chip is near either end the browser clamps naturally at the scroll boundary.
  useEffect(() => {
    if (!stripRef.current || !activeSegmentId) return;
    const activeChip = stripRef.current.querySelector<HTMLElement>(
      `[data-segment-id="${activeSegmentId}"]`,
    );
    activeChip?.scrollIntoView({ block: 'nearest', inline: 'center' });
  }, [activeSegmentId]);

  // Auto-scroll the proximity chip to center as playback moves through scan mode.
  // Only fires when the proximity chip ID changes (segment boundary crossing), not
  // on every currentTime tick, so scroll is infrequent and never fights user input.
  // Suppressed when a segment is explicitly selected (activeSegmentId is set).
  useEffect(() => {
    if (!stripRef.current || activeSegmentId || !proximitySegmentId) return;
    const proximityChip = stripRef.current.querySelector<HTMLElement>(
      `[data-segment-id="${proximitySegmentId}"]`,
    );
    proximityChip?.scrollIntoView({ block: 'nearest', inline: 'center' });
  }, [proximitySegmentId, activeSegmentId]);

  const segmentIds = segments.map((s) => s.id).join(',');

  useEffect(() => {
    let cancelled = false;

    if (segments.length === 0) {
      setExplanationBySegment({});
      return;
    }

    const loadExplanationStatus = async () => {
      try {
        const ids = segments.map((s) => s.id);
        const status = await explainerService.getExplanationStatus(ids);
        if (cancelled) return;
        const numericStatus: Record<number, boolean> = {};
        for (const [k, v] of Object.entries(status)) {
          numericStatus[Number(k)] = v;
        }
        setExplanationBySegment(numericStatus);
      } catch {
        if (cancelled) return;
        setExplanationBySegment({});
      }
    };

    loadExplanationStatus();

    return () => {
      cancelled = true;
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [segmentIds]);

  if (isLoading) {
    return (
      <Box sx={{ px: 2, py: 1.5 }}>
        <Typography variant="overline" color="text.secondary" gutterBottom display="block">
          {t('segments')}
        </Typography>
        <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
          {[0, 1, 2].map((i) => (
            <Skeleton key={i} variant="rounded" width={90} height={32} />
          ))}
        </Stack>
      </Box>
    );
  }

  if (segments.length === 0) {
    return (
      <Box sx={{ px: 2, py: 1.5 }}>
        <Typography variant="overline" color="text.secondary" display="block">
          {t('segments')}
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
          {t('noSegments')}
        </Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ px: 2, py: 1.5 }}>
      {/* Strip header: "Segments" label + optional Resume + optional Download buttons */}
      <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 0.5 }}>
        <Typography variant="overline" color="text.secondary" display="block">
          {t('segments')}
        </Typography>
        <Stack direction="row" alignItems="center">
          {resumeSegment != null && (
            <Tooltip title={t('resumeLastSegment')}>
              <IconButton
                size="small"
                aria-label={t('resumeLastSegment')}
                onClick={() => onSegmentClick(resumeSegment)}
                sx={{ minWidth: 44, minHeight: 44 }}
              >
                <ReplayIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          )}
          {onDownloadTranscripts != null && (
            <Tooltip title={t('downloadTranscripts', 'Download transcripts for practice reading')}>
              <IconButton
                size="small"
                aria-label={t('downloadTranscripts', 'Download transcripts for practice reading')}
                onClick={onDownloadTranscripts}
                sx={{ minWidth: 44, minHeight: 44 }}
              >
                <FileDownloadIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          )}
        </Stack>
      </Stack>
      {/* Horizontal scrollable strip — no wrapping so chips never reflow to multiple rows (AC 4). */}
      <Stack
        ref={stripRef}
        direction="row"
        spacing={1}
        role="tablist"
        aria-label={t('segments')}
        sx={{ overflowX: 'auto', flexWrap: 'nowrap', pt: 0.5, pb: 0.5 }}
      >
        {segments.map((seg, index) => {
          const segId = String(seg.id);
          const isActive = activeSegmentId === segId;
          const hasExplanation = explanationBySegment[seg.id];
          const chipState = getChipState(segId, activeSegmentId, lastWorkedSegmentId, proximitySegmentId);
          const { color, variant, extraSx } = chipStyleForState(chipState);

          const label = t('segmentTime', {
            start: formatTime(seg.start_time),
            end: formatTime(seg.end_time),
          });
          // Roving tabindex: active chip (or first chip when none active) is reachable via Tab;
          // arrow keys move focus within the strip (AC 6).
          const tabIndex = activeSegmentId ? (isActive ? 0 : -1) : index === 0 ? 0 : -1;

          const duration = seg.end_time - seg.start_time;

          const primaryTextColor = isActive ? 'common.white' : 'text.primary';
          const secondaryTextColor = isActive ? 'rgba(255, 255, 255, 0.8)' : 'text.secondary';
          const chipLabel = (
            <Stack direction="column" alignItems="center" spacing={0} sx={{ py: 0.25 }}>
              <Typography variant="body2" sx={{ lineHeight: 1.2, color: primaryTextColor }}>
                {label}
              </Typography>
              <Typography variant="caption" sx={{ lineHeight: 1.2, color: secondaryTextColor }}>
                {formatDuration(duration)}
              </Typography>
              {hasExplanation && (
                <Typography variant="caption" sx={{ lineHeight: 1.2, color: secondaryTextColor }}>
                  {t('explainedChip', 'Explained')}
                </Typography>
              )}
            </Stack>
          );

          const chipLabelWithIcons = (
            <Stack direction="row" alignItems="center" spacing={0.5}>
              {onEditSegment && (
                <Box
                  component="span"
                  role="button"
                  aria-label={t('editSegment', 'Edit Segment')}
                  onClick={(e) => { e.stopPropagation(); onEditSegment(seg); }}
                  sx={{ display: 'inline-flex', alignItems: 'center', cursor: 'pointer', p: 0.25 }}
                >
                  <EditIcon fontSize="small" />
                </Box>
              )}
              {chipLabel}
              <Box
                component="span"
                role="button"
                aria-label={t('deleteSegment')}
                onClick={(e) => { e.stopPropagation(); onDeleteSegment(seg); }}
                sx={{ display: 'inline-flex', alignItems: 'center', cursor: 'pointer', p: 0.25 }}
              >
                <DeleteIcon fontSize="small" />
              </Box>
            </Stack>
          );

          return (
            <Box key={seg.id} data-chip-state={chipState} sx={{ flexShrink: 0 }}>
              <Chip
                data-segment-id={segId}
                label={chipLabelWithIcons}
                onClick={() => onSegmentClick(seg)}
                onKeyDown={(event) => {
                  // Left/Right Arrow — move focus between chips (AC 6).
                  // Enter/Space are NOT handled here: MUI Chip's ButtonBase already
                  // fires onClick on Enter/Space, so the onClick handler covers AC 6 selection.
                  if (event.key === 'ArrowRight') {
                    event.preventDefault();
                    const tabs =
                      stripRef.current?.querySelectorAll<HTMLElement>('[role="tab"]');
                    if (tabs) (tabs[(index + 1) % tabs.length])?.focus();
                  } else if (event.key === 'ArrowLeft') {
                    event.preventDefault();
                    const tabs =
                      stripRef.current?.querySelectorAll<HTMLElement>('[role="tab"]');
                    if (tabs) (tabs[(index - 1 + tabs.length) % tabs.length])?.focus();
                  }
                }}
                color={color}
                variant={variant}
                size="medium"
                role="tab"
                aria-selected={isActive}
                aria-label={label}
                tabIndex={tabIndex}
                sx={{ minHeight: 56, cursor: 'pointer', ...extraSx }}
              />
            </Box>
          );
        })}
      </Stack>
    </Box>
  );
};
